/*
  NCF product buy box (sections/ncf-main-product.liquid).
  - Thumbnails swap the framed image.
  - Quantity and variant changes update "Add to cart · $total", the price and the sticky bar.
  - On mobile, the sticky bar appears once the main Add to cart button has scrolled away.
  Adding to cart is the theme's <product-form>; this component never posts to the cart.
*/
if (!customElements.get('ncf-product-buy')) {
  customElements.define(
    'ncf-product-buy',
    class NcfProductBuy extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.atc = this.querySelector('[data-atc]');
        this.atcLabel = this.querySelector('[data-atc-label]');
        this.qty = this.querySelector('[data-qty]');
        this.select = this.querySelector('[data-variant-select]');
        this.sticky = this.querySelector('[data-sticky]');
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

        const variantsJson = this.querySelector('[data-variants]');
        this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];

        this.onThumbClick = this.onThumbClick.bind(this);
        this.onQtyChange = this.updateTotal.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
        if (this.qty) this.qty.addEventListener('change', this.onQtyChange);
        if (this.qty) this.qty.addEventListener('input', this.onQtyChange);
        if (this.select) this.select.addEventListener('change', this.onVariantChange);

        this.observeSticky();
      }

      disconnectedCallback() {
        this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
        if (this.qty) {
          this.qty.removeEventListener('change', this.onQtyChange);
          this.qty.removeEventListener('input', this.onQtyChange);
        }
        if (this.select) this.select.removeEventListener('change', this.onVariantChange);
        if (this.observer) this.observer.disconnect();
      }

      onThumbClick(event) {
        this.showMedia(event.currentTarget.dataset.thumb);
      }

      showMedia(mediaId) {
        if (!mediaId) return;
        this.querySelectorAll('.ncf-pdp__slide[data-media-id]').forEach((slide) => {
          slide.hidden = slide.dataset.mediaId !== String(mediaId);
        });
        this.thumbs.forEach((thumb) => {
          thumb.setAttribute('aria-current', thumb.dataset.thumb === String(mediaId) ? 'true' : 'false');
        });
      }

      currentVariant() {
        if (!this.select) return null;
        return this.variants.find((variant) => String(variant.id) === this.select.value) || null;
      }

      onVariantChange() {
        const variant = this.currentVariant();
        if (!variant) return;

        const current = this.querySelector('[data-price-current]');
        const compare = this.querySelector('[data-price-compare]');
        const compareValue = this.querySelector('[data-price-compare-value]');
        const stickyPrice = this.querySelector('[data-sticky-price]');

        if (current) current.textContent = this.formatMoney(variant.price);
        if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);
        if (compare && compareValue) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !onSale;
          if (onSale) compareValue.textContent = this.formatMoney(variant.compare_at_price);
        }

        this.atc.dataset.price = variant.price;
        if (variant.available) {
          this.atc.disabled = false;
          this.atc.removeAttribute('aria-disabled');
        } else {
          this.atc.disabled = true;
          this.atc.setAttribute('aria-disabled', 'true');
        }

        if (variant.featured_media) this.showMedia(variant.featured_media.id);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        this.updateTotal();
      }

      updateTotal() {
        if (!this.atc || !this.atcLabel) return;
        const variant = this.currentVariant();
        if (variant && !variant.available) {
          this.atcLabel.textContent = this.dataset.soldOutLabel;
          return;
        }
        if (this.atc.disabled) return;

        const quantity = Math.max(parseInt(this.qty ? this.qty.value : 1, 10) || 1, 1);
        const total = parseInt(this.atc.dataset.price, 10) * quantity;
        this.atcLabel.textContent = `${this.dataset.addLabel} · ${this.formatMoney(total)}`;
      }

      // The root is stretched far below the viewport, so the only edge the button can cross
      // is the top one. A jump from below the fold to above it (restored scroll, anchor link)
      // still counts, which a plain viewport observer would miss.
      observeSticky() {
        if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.sticky.hidden = entry.isIntersecting;
          },
          { rootMargin: '0px 0px 100000px 0px' }
        );
        this.observer.observe(this.atc);
      }

      // Shopify money format ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}} …).
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = Number(cents) / 100;
        const group = (number, decimals, thousands, decimal) => {
          const [whole, fraction] = number.toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return group(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return group(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return group(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return group(value, 2, "'", '.');
            default:
              return group(value, 2, ',', '.');
          }
        });
      }
    }
  );
}
